import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useGetMembersQuery,
  useGetContactsQuery,
  useGetDealsQuery,
} from "@/store/rest_api";
import type Task from "@/model/interface/Task";
import { memberName } from "@/util/member";

const taskSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  status: z.string(),
  priority: z.string(),
  dueDate: z.string().optional(),
  assignedMembershipId: z.string().optional(),
  contactId: z.string().optional(),
  dealId: z.string().optional(),
});

type TaskFormValues = z.infer<typeof taskSchema>;

interface TaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: Task | null;
  defaultContactId?: string;
  defaultDealId?: string;
}

const QUIET_TRIGGER =
  "border-transparent bg-transparent px-2 hover:bg-muted/50 focus:bg-background data-[state=open]:bg-background";

const InlineText: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
}> = ({ value, onChange, placeholder, type = "text", multiline }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const enter = () => {
    if (window.getSelection?.()?.toString()) {
      return;
    }
    setDraft(value);
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (draft !== value) {
      onChange(draft);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    if (multiline) {
      return (
        <Textarea
          autoFocus
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              cancel();
            }
          }}
        />
      );
    }
    return (
      <Input
        autoFocus
        type={type}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            commit();
          }
          if (event.key === "Escape") {
            cancel();
          }
        }}
      />
    );
  }

  return (
    <div
      onClick={enter}
      className={`min-h-9 cursor-text rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 ${multiline ? "whitespace-pre-wrap" : ""}`}
    >
      {value || <span className="text-muted-foreground">{placeholder}</span>}
    </div>
  );
};

const TaskFormDialog: React.FC<TaskFormDialogProps> = ({ open, onOpenChange, task, defaultContactId, defaultDealId }) => {
  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const { data: members = [] } = useGetMembersQuery({ status: "ACTIVE" });
  const { data: contacts } = useGetContactsQuery({ size: 100 });
  const { data: deals } = useGetDealsQuery({ size: 100 });

  const isEditing = !!task;
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: "",
      description: "",
      status: "TODO",
      priority: "MEDIUM",
      dueDate: "",
      assignedMembershipId: "",
      contactId: "",
      dealId: "",
    },
  });

  useEffect(() => {
    if (open) {
      if (task) {
        form.reset({
          title: task.title,
          description: task.description ?? "",
          status: task.status,
          priority: task.priority,
          dueDate: task.dueDate ?? "",
          assignedMembershipId: task.assignedMembership?.id?.toString() ?? "",
          contactId: task.contact?.id ?? "",
          dealId: task.deal?.id ?? "",
        });
      } else {
        form.reset({
          title: "",
          description: "",
          status: "TODO",
          priority: "MEDIUM",
          dueDate: "",
          assignedMembershipId: "",
          contactId: defaultContactId ?? "",
          dealId: defaultDealId ?? "",
        });
      }
    }
  }, [open, task]);

  const onSubmit = async (values: TaskFormValues) => {
    const data: any = {
      title: values.title,
      description: values.description?.trim() || null,
      status: values.status,
      priority: values.priority,
      dueDate: values.dueDate || null,
      assignedMembership: values.assignedMembershipId ? { id: Number(values.assignedMembershipId) } : null,
      contact: values.contactId ? { id: values.contactId } : null,
      deal: values.dealId ? { id: values.dealId } : null,
    };

    try {
      if (isEditing) {
        await updateTask({ id: task!.id, data }).unwrap();
        toast.success("Task updated");
      } else {
        await createTask(data).unwrap();
        toast.success("Task created");
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.detail ?? `Failed to ${isEditing ? "update" : "create"} task`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Task" : "New Task"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="title" render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <InlineText value={field.value} onChange={field.onChange} placeholder="Task title..." />
              </FormItem>
            )} />

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <InlineText value={field.value ?? ""} onChange={field.onChange} placeholder="Add details..." multiline />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="priority" render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger className={QUIET_TRIGGER}><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )} />

              <FormField control={form.control} name="status" render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl><SelectTrigger className={QUIET_TRIGGER}><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value="TODO">To Do</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="DONE">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="dueDate" render={({ field }) => (
              <FormItem>
                <FormLabel>Due Date</FormLabel>
                <InlineText value={field.value ?? ""} onChange={field.onChange} placeholder="No due date" type="date" />
              </FormItem>
            )} />

            <FormField control={form.control} name="assignedMembershipId" render={({ field }) => (
              <FormItem>
                <FormLabel>Assigned To</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger className={QUIET_TRIGGER}><SelectValue placeholder="Unassigned" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="-">Unassigned</SelectItem>
                    {members.map((member) => (
                      <SelectItem key={member.id} value={String(member.id)}>
                        {memberName(member)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="contactId" render={({ field }) => (
              <FormItem>
                <FormLabel>Contact</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger className={QUIET_TRIGGER}><SelectValue placeholder="None" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="-">None</SelectItem>
                    {contacts?.items.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "Unnamed"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="dealId" render={({ field }) => (
              <FormItem>
                <FormLabel>Deal</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger className={QUIET_TRIGGER}><SelectValue placeholder="None" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="-">None</SelectItem>
                    {deals?.items.map((deal) => (
                      <SelectItem key={deal.id} value={deal.id}>
                        {deal.propertyAddress || "Untitled deal"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {isEditing ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskFormDialog;
